import React, { FunctionComponent, Fragment, useEffect, useState } from 'react';
import { Flex } from 'mdlkit';
import { useRouter } from 'next/router';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  EligibilityCheckResponse,
  AffiliationType,
  PatientSubmitError,
} from '../../../interfaces/register';
import IneligibleReason from './IneligibleReason';
import { AppCardFooter, NextHyperlink } from '../../shared';

import { event } from '../../../lib/analytics';
import { REGISTRATION } from '../../../constants/events';
import { isCoverageCheckFlow } from '../../../lib/coverageCheckStore';

interface Props {
  eligibilityCheck?: EligibilityCheckResponse;
  onContinue(
    affiliationType: AffiliationType
  ): Promise<void | boolean | PatientSubmitError>;
}

const IneligibleError: FunctionComponent<Props> = ({
  eligibilityCheck,
  onContinue,
}: Props) => {
  const router = useRouter();
  const { formatMessage: f } = useIntl();

  const isBranded = typeof router.query.affiliation !== 'undefined';

  // Detect the coverage-check flow client-side only to avoid an SSR/hydration mismatch.
  // When set, "Retry" routes to /insurance instead of register-review.
  const [coverageFlow, setCoverageFlow] = useState<boolean>(false);
  useEffect(() => {
    setCoverageFlow(isCoverageCheckFlow());
  }, []);

  const retryHref = coverageFlow
    ? {
        pathname: `/insurance`,
        query: { zip: eligibilityCheck?.zip || '' },
      }
    : {
        pathname: `register-review`,
        query: {
          ...router.query,
          eligibilityCheckId: eligibilityCheck?.token,
        },
      };

  return (
    <Fragment>
      <Flex column align="center">
        <IneligibleReason
          showContinueWithoutCoverageButton={Boolean(
            eligibilityCheck && !eligibilityCheck.disableIneligibleRegistration
          )}
          eligibilityCheck={eligibilityCheck}
          onContinue={onContinue}
        />
      </Flex>
      <AppCardFooter>
        <NextHyperlink
          data-testid="retryLink"
          href={retryHref}
          bold
          onClick={() =>
            event({
              action: 'retry_eligibility_check',
              category: REGISTRATION,
            })
          }
          data-track-event
          data-track={JSON.stringify({
            controlName: 'retry-without-coverage-link',
            controlText: f({
              id: 'retryButton',
            }),
          })}
        >
          <FormattedMessage id="retryButton" />
        </NextHyperlink>
        {!isBranded && eligibilityCheck?.disableIneligibleRegistration && (
          <NextHyperlink
            mt="sm"
            data-testid="skipLink"
            href={{
              pathname: `skip-benefit`,
              query: {
                ...router.query,
                zip: (eligibilityCheck && eligibilityCheck.zip) || '',
              },
            }}
            bold
            onClick={() =>
              event({
                action: 'click_skip',
                category: REGISTRATION,
                label: 'location',
                value: 'eligibility-error',
              })
            }
          >
            <FormattedMessage id="register.skipButton" />
          </NextHyperlink>
        )}
      </AppCardFooter>
    </Fragment>
  );
};

export default IneligibleError;
